import React, { useState, useEffect, useCallback } from 'react';
import { FiClock } from 'react-icons/fi';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaForward,
  FaSyncAlt,
  FaTrophy,
  FaInfoCircle,
  FaAngleDown,
  FaAngleUp,
  FaList,
  FaBolt,
  FaUnlock,
  FaAngleLeft,
  FaAngleRight,
} from 'react-icons/fa';
import apiService from '../services/apiService';

interface SchedulerJob {
  jobId: string;
  jobName: string;
  status: string;
  message?: string;
  startedAt: string;
  pickedUpAt?: string;
  completedAt?: string;
  duration?: string;
  details?: any;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type TabType = 'all' | 'active';
type ViewMode = 'timeline' | 'list';

const SchedulerJobs: React.FC = () => {
  const [jobs, setJobs] = useState<SchedulerJob[]>([]);
  const [activeJobs, setActiveJobs] = useState<SchedulerJob[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [errorsOnly, setErrorsOnly] = useState(false);
  // Default: hide watchdog so the signal isn't buried under health-check runs.
  const [jobFilter, setJobFilter] = useState<string>('exclude-watchdog');
  const [seriesFilter, setSeriesFilter] = useState<string>('');
  const [matchFilter, setMatchFilter] = useState<string>('');
  const [allSeries, setAllSeries] = useState<Array<{ seriesId: string; name: string }>>([]);
  const [seriesMatches, setSeriesMatches] = useState<Array<{ matchId: string; label: string }>>([]);
  // Page-size picker — bumped defaults so low-frequency jobs aren't drowned.
  const [pageSize, setPageSize] = useState<number>(200);

  const JOB_FILTER_OPTIONS = [
    { value: 'all', label: 'All types' },
    { value: 'exclude-watchdog', label: 'All except watchdog' },
    { value: 'match-tick', label: 'match-tick' },
    { value: 'watchdog-live-matches', label: 'watchdog' },
    { value: 'sync-scorecards', label: 'sync-scorecards' },
    { value: 'update-fantasy-points', label: 'update-fantasy-points' },
    { value: 'process-unlocking-only', label: 'unlock' },
    { value: 'ingest-match-schedule', label: 'ingest-match-schedule' },
  ];

  const loadJobs = useCallback(async (page: number = 1) => {
    setLoading(true);
    setError('');

    let activeData: any[] = [];

    // Page size honors the user's picker. Timeline defaults high (200)
    // so low-frequency jobs aren't drowned by watchdog; list defaults lower.
    const limit = pageSize;
    // 'exclude-watchdog' has no API equivalent — fetch all then filter client
    // side. Other specific types are pushed to the backend filter.
    const apiJobName =
      jobFilter === 'all' || jobFilter === 'exclude-watchdog' ? undefined : jobFilter;

    try {
      const result = await apiService.getSchedulerJobs(page, limit, apiJobName);
      let jobsList = result?.jobs || [];
      if (jobFilter === 'exclude-watchdog') {
        jobsList = jobsList.filter((j: SchedulerJob) => !j.jobName.includes('watchdog'));
      }
      setJobs(Array.isArray(jobsList) ? jobsList : []);
      setPagination({
        total: result?.total || 0,
        page: result?.page || 1,
        limit: result?.limit || limit,
        totalPages: result?.totalPages || 0,
      });
    } catch (err: any) {
      console.error('Error loading scheduler jobs:', err);
      setError(err?.message || 'Failed to load scheduler jobs');
    }

    try {
      activeData = await apiService.getActiveJobs();
    } catch (err: any) {
      console.error('Error loading active jobs:', err);
    }

    setActiveJobs(Array.isArray(activeData) ? activeData : []);
    setLoading(false);
  }, [pageSize, jobFilter]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Load the published-series list once so the series dropdown is stable
  // regardless of what's on the current scheduler-jobs page.
  useEffect(() => {
    let cancelled = false;
    apiService.getSeries().then((resp: any) => {
      const raw = resp?.data || resp || [];
      const list = (Array.isArray(raw) ? raw : [])
        .filter((s: any) => s.publish === true)
        .map((s: any) => ({
          seriesId: String(s.seriesId || s.id),
          name: s.name || s.title || String(s.seriesId || s.id),
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
      if (!cancelled) setAllSeries(list);
    }).catch(() => {
      if (!cancelled) setAllSeries([]);
    });
    return () => { cancelled = true; };
  }, []);

  // Load matches for the selected series so the match dropdown is stable.
  useEffect(() => {
    if (!seriesFilter) {
      setSeriesMatches([]);
      return;
    }
    let cancelled = false;
    apiService.getMatches(seriesFilter).then((matches: any) => {
      const list = (Array.isArray(matches) ? matches : [])
        .map((m: any) => {
          const mid = String(m.matchId);
          const local = m.localTeam?.teamSName || m.localTeam?.teamName || '?';
          const visitor = m.visitorTeam?.teamSName || m.visitorTeam?.teamName || '?';
          return { matchId: mid, label: `#${mid} · ${local} v ${visitor}` };
        })
        .sort((a: any, b: any) => Number(a.matchId) - Number(b.matchId));
      if (!cancelled) setSeriesMatches(list);
    }).catch(() => {
      if (!cancelled) setSeriesMatches([]);
    });
    return () => { cancelled = true; };
  }, [seriesFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setExpandedJobId(null);
    loadJobs(newPage);
  };

  const toggleJobExpand = (job: SchedulerJob) => {
    setExpandedJobId(expandedJobId === job.jobId ? null : job.jobId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getJobIcon = (jobName: string) => {
    if (jobName.includes('match-tick')) return FaBolt;
    if (jobName.includes('sync-scorecard')) return FaSyncAlt;
    if (jobName.includes('fantasy-points')) return FaTrophy;
    if (jobName.includes('unlock')) return FaUnlock;
    return FiClock;
  };

  const getJobColor = (jobName: string) => {
    if (jobName.includes('match-tick')) return { bg: 'bg-rose-50 dark:bg-rose-500/10', border: 'border-rose-100 dark:border-rose-500/20', icon: 'text-rose-600 dark:text-rose-400' };
    if (jobName.includes('sync-scorecard')) return { bg: 'bg-blue-50 dark:bg-primary-500/10', border: 'border-blue-100 dark:border-primary-500/20', icon: 'text-blue-600 dark:text-primary-400' };
    if (jobName.includes('fantasy-points')) return { bg: 'bg-amber-50 dark:bg-warning-500/10', border: 'border-amber-100 dark:border-warning-500/20', icon: 'text-amber-600 dark:text-warning-400' };
    if (jobName.includes('unlock')) return { bg: 'bg-purple-50 dark:bg-secondary-500/10', border: 'border-purple-100 dark:border-secondary-500/20', icon: 'text-purple-600 dark:text-secondary-400' };
    return { bg: 'bg-indigo-50 dark:bg-indigo-500/10', border: 'border-indigo-100 dark:border-indigo-500/20', icon: 'text-indigo-600 dark:text-indigo-400' };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
      case 'completed':
        return 'bg-emerald-100 dark:bg-success-500/20 text-emerald-700 dark:text-success-400 border-emerald-200 dark:border-success-500/30';
      case 'failed':
      case 'error':
        return 'bg-red-100 dark:bg-danger-500/20 text-red-700 dark:text-danger-400 border-red-200 dark:border-danger-500/30';
      case 'running':
      case 'active':
        return 'bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border-blue-200 dark:border-primary-500/30';
      default:
        return 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-500/30';
    }
  };

  // Extract the kanban data from the nested details structure
  const getKanbanData = (job: SchedulerJob) => {
    const d = job.details;
    if (!d) return null;

    // For update-fantasy-points and sync-scorecards: details.details contains succeeded/failed/skipped
    const inner = d.details || {};
    const succeeded = inner.succeeded || d.succeeded || [];
    const failed = inner.failed || d.failed || d.errors || [];
    const skipped = inner.skipped || d.skipped || [];
    const series = d.series || [];

    // For process-unlocking-only
    const unlockedContests = d.unlockedContests || [];
    const errors = d.errors || [];

    return { succeeded, failed, skipped, series, unlockedContests, errors };
  };

  const tabs = [
    { id: 'all' as TabType, label: 'All Jobs', icon: FaList, count: pagination.total },
    { id: 'active' as TabType, label: 'Active Jobs', icon: FaBolt, count: activeJobs.length },
  ];

  // ---- Series / match filter derivation ----
  // Options are derived from what's currently in the fetched job page so the
  // dropdowns stay relevant. Selecting a value filters before grouping/render.

  interface SeriesOption { seriesId: string; name: string }
  interface MatchOption { matchId: string; label: string; seriesId?: string }

  // Prefer the stable fetched lists (published series + matches-by-series).
  // Fall back to client-deriving from the current jobs page when the fetch
  // hasn't returned yet or returned empty — never leaves the user filterless.
  const seriesOptions: SeriesOption[] = allSeries.length > 0
    ? allSeries
    : (() => {
        const map = new Map<string, string>();
        for (const j of jobs) {
          const d = j.details || {};
          const sid = d.seriesId ? String(d.seriesId) : '';
          if (!sid) continue;
          if (!map.has(sid)) map.set(sid, d.seriesName || sid);
        }
        return Array.from(map.entries())
          .map(([seriesId, name]) => ({ seriesId, name }))
          .sort((a, b) => a.name.localeCompare(b.name));
      })();

  const matchOptions: MatchOption[] = seriesFilter && seriesMatches.length > 0
    ? seriesMatches
    : (() => {
        const map = new Map<string, MatchOption>();
        for (const j of jobs) {
          const d = j.details || {};
          const mid = d.matchId ? String(d.matchId) : '';
          if (!mid) continue;
          const sid = d.seriesId ? String(d.seriesId) : '';
          if (seriesFilter && sid !== seriesFilter) continue;
          if (!map.has(mid)) {
            const desc = d.matchDesc || `Match ${mid}`;
            map.set(mid, { matchId: mid, label: `#${mid} · ${desc}`, seriesId: sid });
          }
        }
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
      })();

  const jobMatchesFilters = (j: SchedulerJob): boolean => {
    const d = j.details || {};
    if (seriesFilter) {
      if (String(d.seriesId || '') !== seriesFilter) return false;
    }
    if (matchFilter) {
      if (String(d.matchId || '') !== matchFilter) return false;
    }
    return true;
  };

  const filteredJobs = jobs.filter(jobMatchesFilters);

  // ---- Timeline view helpers ----

  interface MatchGroup {
    matchId: number;
    matchDesc: string;
    seriesName: string;
    localTeam: string;
    visitorTeam: string;
    state: string;
    isLive: boolean;
    runs: SchedulerJob[];
  }

  interface PeriodicJobGroup {
    jobName: string;
    runs: SchedulerJob[];
  }

  const groupJobsByMatch = (allJobs: SchedulerJob[]) => {
    const groups = new Map<number, MatchGroup>();
    const periodicGroups = new Map<string, PeriodicJobGroup>();

    for (const job of allJobs) {
      const isMatchTick = job.jobName.includes('match-tick');
      const d = job.details || {};
      const mid = Number(d.matchId);
      if (!isMatchTick || !mid || Number.isNaN(mid)) {
        const existing = periodicGroups.get(job.jobName);
        if (existing) existing.runs.push(job);
        else periodicGroups.set(job.jobName, { jobName: job.jobName, runs: [job] });
        continue;
      }
      const existing = groups.get(mid);
      if (existing) {
        existing.runs.push(job);
        // Latest job wins for state display — jobs come back desc by startedAt
        // so the first seen is the most recent.
        if (existing.runs.length === 1) {
          existing.state = d.state || existing.state;
          existing.isLive = !!d.isLive;
        }
      } else {
        groups.set(mid, {
          matchId: mid,
          matchDesc: d.matchDesc || `Match ${mid}`,
          seriesName: d.seriesName || d.seriesId || 'Unknown series',
          localTeam: d.localTeam || '?',
          visitorTeam: d.visitorTeam || '?',
          state: d.state || 'Unknown',
          isLive: !!d.isLive,
          runs: [job],
        });
      }
    }

    // Sort runs in each group chronologically (oldest → newest, left → right)
    const groupList: MatchGroup[] = Array.from(groups.values());
    groupList.forEach((g: MatchGroup) => {
      g.runs.sort((a: SchedulerJob, b: SchedulerJob) => {
        const aMs = new Date(a.startedAt).getTime();
        const bMs = new Date(b.startedAt).getTime();
        return aMs - bMs;
      });
    });

    // Sort groups: live first, then by most-recent-run desc
    const sorted = groupList.sort((a: MatchGroup, b: MatchGroup) => {
      if (a.isLive !== b.isLive) return a.isLive ? -1 : 1;
      const aLast = new Date(a.runs[a.runs.length - 1]?.startedAt || 0).getTime();
      const bLast = new Date(b.runs[b.runs.length - 1]?.startedAt || 0).getTime();
      return bLast - aLast;
    });

    // Sort periodic groups' runs chronologically, then by most-recent-run desc
    const periodicList: PeriodicJobGroup[] = Array.from(periodicGroups.values());
    periodicList.forEach((g: PeriodicJobGroup) => {
      g.runs.sort((a: SchedulerJob, b: SchedulerJob) => {
        return new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();
      });
    });
    periodicList.sort((a: PeriodicJobGroup, b: PeriodicJobGroup) => {
      const aLast = new Date(a.runs[a.runs.length - 1]?.startedAt || 0).getTime();
      const bLast = new Date(b.runs[b.runs.length - 1]?.startedAt || 0).getTime();
      return bLast - aLast;
    });

    return { matches: sorted, periodic: periodicList };
  };

  const dotColor = (status: string) => {
    switch (status) {
      case 'success':
      case 'completed':
        return 'bg-emerald-500 hover:bg-emerald-600';
      case 'error':
      case 'failed':
        return 'bg-red-500 hover:bg-red-600 ring-2 ring-red-200 dark:ring-red-500/30';
      case 'in_progress':
      case 'running':
      case 'active':
        return 'bg-blue-500 hover:bg-blue-600 animate-pulse';
      default:
        return 'bg-gray-400 hover:bg-gray-500';
    }
  };

  const formatHourMin = (iso: string) => {
    const d = new Date(iso);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const renderTimeline = () => {
    const { matches, periodic } = groupJobsByMatch(filteredJobs);
    const filteredMatches = errorsOnly
      ? matches.filter((g) => g.runs.some((r) => r.status === 'error' || r.status === 'failed'))
      : matches;
    const filteredPeriodic = errorsOnly
      ? periodic.filter((g) => g.runs.some((r) => r.status === 'error' || r.status === 'failed'))
      : periodic;

    return (
      <div className="space-y-6">
        {/* Match timelines */}
        {filteredMatches.length === 0 && filteredPeriodic.length === 0 ? (
          <div className="text-center py-12">
            <FiClock className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No jobs match filters</h3>
            <p className="text-gray-600 dark:text-gray-400">Try turning off "Errors only" or refreshing.</p>
          </div>
        ) : (
          <>
            {filteredMatches.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                  Match Ticks · {filteredMatches.length} {filteredMatches.length === 1 ? 'match' : 'matches'}
                </h2>
                <div className="space-y-3">
                  {filteredMatches.map((g) => {
                    const errorCount = g.runs.filter((r) => r.status === 'error' || r.status === 'failed').length;
                    return (
                      <div
                        key={g.matchId}
                        className="bg-white dark:bg-dark-400/60 rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm p-4"
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {g.isLive && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400">
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500" />
                              </span>
                              LIVE
                            </span>
                          )}
                          <span className="text-sm font-bold text-gray-900 dark:text-white">
                            {g.localTeam} v {g.visitorTeam}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            · {g.seriesName}
                          </span>
                          <span className="text-[10px] font-mono text-gray-400">#{g.matchId}</span>
                          <span className="ml-auto text-xs font-semibold text-gray-600 dark:text-gray-400">
                            {g.runs.length} {g.runs.length === 1 ? 'run' : 'runs'}
                          </span>
                          {errorCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400">
                              <FaTimesCircle className="h-2.5 w-2.5" />
                              {errorCount} failed
                            </span>
                          )}
                          <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-dark-300/40 px-2 py-0.5 rounded-full">
                            {g.state}
                          </span>
                        </div>

                        {/* Dots timeline — horizontally scrollable */}
                        <div className="flex items-center gap-1 overflow-x-auto pb-2">
                          {g.runs.map((run) => {
                            const isExpanded = expandedJobId === run.jobId;
                            return (
                              <button
                                key={run.jobId}
                                onClick={() => toggleJobExpand(run)}
                                title={`${formatHourMin(run.startedAt)} · ${run.status}${run.message ? ' · ' + run.message : ''}`}
                                className={`h-3 w-3 rounded-full flex-shrink-0 transition-all ${dotColor(run.status)} ${
                                  isExpanded ? 'ring-2 ring-offset-1 ring-indigo-500 scale-125' : ''
                                }`}
                                aria-label={`Run at ${formatHourMin(run.startedAt)}`}
                              />
                            );
                          })}
                          {g.runs.length > 0 && (
                            <span className="text-[10px] text-gray-400 ml-2 whitespace-nowrap">
                              {formatHourMin(g.runs[0].startedAt)} → {formatHourMin(g.runs[g.runs.length - 1].startedAt)}
                            </span>
                          )}
                        </div>

                        {/* Inline detail for the clicked dot */}
                        {g.runs.find((r) => r.jobId === expandedJobId) && (
                          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-dark-300/40">
                            {(() => {
                              const run = g.runs.find((r) => r.jobId === expandedJobId)!;
                              return (
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                                      {formatDate(run.startedAt)}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusColor(run.status)}`}>
                                      {run.status}
                                    </span>
                                    {run.duration && (
                                      <span className="text-[10px] text-gray-500">{run.duration}</span>
                                    )}
                                  </div>
                                  {run.message && (
                                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{run.message}</p>
                                  )}
                                  {renderKanbanBoard(run)}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Periodic jobs (watchdog, sync-scorecard, etc.) — one row per
                job name with a dot timeline, so high-frequency jobs don't
                flood the page. */}
            {filteredPeriodic.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                  Periodic Jobs · {filteredPeriodic.length}
                </h2>
                <div className="space-y-3">
                  {filteredPeriodic.map((g) => {
                    const errorCount = g.runs.filter((r) => r.status === 'error' || r.status === 'failed').length;
                    const colors = getJobColor(g.jobName);
                    const Icon = getJobIcon(g.jobName);
                    const latest = g.runs[g.runs.length - 1];
                    return (
                      <div
                        key={g.jobName}
                        className={`rounded-xl border ${colors.border} ${colors.bg} p-4`}
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <Icon className={`h-4 w-4 ${colors.icon}`} />
                          <span className="text-sm font-bold text-gray-900 dark:text-white">
                            {g.jobName}
                          </span>
                          <span className="ml-auto text-xs font-semibold text-gray-600 dark:text-gray-400">
                            {g.runs.length} {g.runs.length === 1 ? 'run' : 'runs'}
                          </span>
                          {errorCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400">
                              <FaTimesCircle className="h-2.5 w-2.5" />
                              {errorCount} failed
                            </span>
                          )}
                          {latest && (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusColor(latest.status)}`}>
                              latest: {latest.status}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1 overflow-x-auto pb-2">
                          {g.runs.map((run) => {
                            const isExpanded = expandedJobId === run.jobId;
                            return (
                              <button
                                key={run.jobId}
                                onClick={() => toggleJobExpand(run)}
                                title={`${formatHourMin(run.startedAt)} · ${run.status}${run.message ? ' · ' + run.message : ''}`}
                                className={`h-3 w-3 rounded-full flex-shrink-0 transition-all ${dotColor(run.status)} ${
                                  isExpanded ? 'ring-2 ring-offset-1 ring-indigo-500 scale-125' : ''
                                }`}
                                aria-label={`Run at ${formatHourMin(run.startedAt)}`}
                              />
                            );
                          })}
                          {g.runs.length > 0 && (
                            <span className="text-[10px] text-gray-400 ml-2 whitespace-nowrap">
                              {formatHourMin(g.runs[0].startedAt)} → {formatHourMin(g.runs[g.runs.length - 1].startedAt)}
                            </span>
                          )}
                        </div>

                        {g.runs.find((r) => r.jobId === expandedJobId) && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-300/40">
                            {(() => {
                              const run = g.runs.find((r) => r.jobId === expandedJobId)!;
                              return (
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                                      {formatDate(run.startedAt)}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusColor(run.status)}`}>
                                      {run.status}
                                    </span>
                                    {run.duration && (
                                      <span className="text-[10px] text-gray-500">{run.duration}</span>
                                    )}
                                  </div>
                                  {run.message && (
                                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{run.message}</p>
                                  )}
                                  {renderKanbanBoard(run)}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render Kanban board for a job's details
  const renderKanbanBoard = (job: SchedulerJob) => {
    const kanban = getKanbanData(job);
    if (!kanban) return null;

    const isUnlocking = job.jobName.includes('unlock');

    // For unlocking jobs, show a simpler view
    if (isUnlocking) {
      return (
        <div className="mt-4 space-y-3">
          {job.details?.message && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{job.details.message}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border-2 border-emerald-300 dark:border-emerald-600 overflow-hidden">
              <div className="bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaUnlock className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
                  <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Unlocked</span>
                </div>
                <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {kanban.unlockedContests.length}
                </span>
              </div>
              <div className="p-3 bg-white/50 dark:bg-dark-500/30">
                {kanban.unlockedContests.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-sm italic">No contests unlocked</div>
                ) : (
                  <div className="space-y-2">
                    {kanban.unlockedContests.map((c: any, idx: number) => (
                      <div key={idx} className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm">
                        <pre className="text-[10px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{JSON.stringify(c, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-xl border-2 border-red-300 dark:border-red-600 overflow-hidden">
              <div className="bg-red-50 dark:bg-red-950/40 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaTimesCircle className="h-4 w-4 text-red-700 dark:text-red-400" />
                  <span className="text-sm font-bold text-red-700 dark:text-red-400">Errors</span>
                </div>
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {kanban.errors.length}
                </span>
              </div>
              <div className="p-3 bg-white/50 dark:bg-dark-500/30">
                {kanban.errors.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-sm italic">No errors</div>
                ) : (
                  <div className="space-y-2">
                    {kanban.errors.map((e: any, idx: number) => (
                      <div key={idx} className="bg-white dark:bg-dark-400/60 rounded-lg border border-red-200 dark:border-red-500/20 p-3 shadow-sm text-xs text-red-700 dark:text-red-400">
                        {typeof e === 'string' ? e : JSON.stringify(e)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // For sync-scorecards / update-fantasy-points
    const columns = [
      {
        title: 'Succeeded',
        items: kanban.succeeded,
        icon: FaCheckCircle,
        borderColor: 'border-emerald-300 dark:border-emerald-600',
        headerBg: 'bg-emerald-50 dark:bg-emerald-950/40',
        headerText: 'text-emerald-700 dark:text-emerald-400',
        badgeBg: 'bg-emerald-500',
      },
      {
        title: 'Failed',
        items: kanban.failed,
        icon: FaTimesCircle,
        borderColor: 'border-red-300 dark:border-red-600',
        headerBg: 'bg-red-50 dark:bg-red-950/40',
        headerText: 'text-red-700 dark:text-red-400',
        badgeBg: 'bg-red-500',
      },
      {
        title: 'Skipped',
        items: kanban.skipped,
        icon: FaForward,
        borderColor: 'border-amber-300 dark:border-amber-600',
        headerBg: 'bg-amber-50 dark:bg-amber-950/40',
        headerText: 'text-amber-700 dark:text-amber-400',
        badgeBg: 'bg-amber-500',
      },
    ];

    return (
      <div className="mt-4">
        {/* Summary message */}
        {job.details?.message && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{job.details.message}</p>
        )}

        {/* Series tags */}
        {kanban.series.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {kanban.series.map((s: any) => (
              <span
                key={s.seriesId}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border border-blue-200 dark:border-primary-500/30"
              >
                {s.seriesName}
              </span>
            ))}
          </div>
        )}

        {/* Summary stats row — summary fields take priority; fall back to
            kanban array lengths so counts still render when the backend
            response drops the aggregated fields. */}
        {job.details && (() => {
          const succeededCount =
            job.details.matchesProcessed ??
            job.details.contestsProcessed ??
            kanban.succeeded.length;
          const skippedCount =
            job.details.contestsSkipped ?? kanban.skipped.length;
          const failedCount =
            job.details.matchesFailed ??
            (kanban.failed.length + kanban.errors.length);
          const hasAnyStat =
            job.details.seriesProcessed != null ||
            job.details.matchesRefreshed != null ||
            succeededCount > 0 ||
            skippedCount > 0 ||
            failedCount > 0 ||
            (job.details.liveContestsUpdated ?? 0) > 0;
          if (!hasAnyStat) return null;
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {job.details.seriesProcessed != null && (
                <div className="text-center p-2 bg-gray-50 dark:bg-dark-300/30 rounded-lg">
                  <p className="text-lg font-bold text-gray-800 dark:text-gray-200">{job.details.seriesProcessed}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Series</p>
                </div>
              )}
              {job.details.matchesRefreshed != null && (
                <div className="text-center p-2 bg-blue-50 dark:bg-primary-500/10 rounded-lg">
                  <p className="text-lg font-bold text-blue-700 dark:text-primary-400">{job.details.matchesRefreshed}</p>
                  <p className="text-[10px] text-blue-600 dark:text-primary-500">Refreshed</p>
                </div>
              )}
              {succeededCount > 0 && (
                <div className="text-center p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg">
                  <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{succeededCount}</p>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-500">Processed</p>
                </div>
              )}
              {skippedCount > 0 && (
                <div className="text-center p-2 bg-amber-50 dark:bg-amber-500/10 rounded-lg">
                  <p className="text-lg font-bold text-amber-700 dark:text-amber-400">{skippedCount}</p>
                  <p className="text-[10px] text-amber-600 dark:text-amber-500">Skipped</p>
                </div>
              )}
              {(job.details.liveContestsUpdated ?? 0) > 0 && (
                <div className="text-center p-2 bg-emerald-50 dark:bg-[#13EC5B]/10 rounded-lg">
                  <p className="text-lg font-bold text-emerald-700 dark:text-[#13EC5B] flex items-center justify-center gap-1">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#13EC5B] opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#13EC5B]" />
                    </span>
                    {job.details.liveContestsUpdated}
                  </p>
                  <p className="text-[10px] text-emerald-600 dark:text-[#13EC5B]">Live Updated</p>
                </div>
              )}
              {failedCount > 0 && (
                <div className="text-center p-2 bg-red-50 dark:bg-red-500/10 rounded-lg">
                  <p className="text-lg font-bold text-red-700 dark:text-red-400">{failedCount}</p>
                  <p className="text-[10px] text-red-600 dark:text-red-500">Failed</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Kanban Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {columns.map((col) => {
            const Icon = col.icon;
            return (
              <div key={col.title} className={`rounded-xl border-2 ${col.borderColor} overflow-hidden`}>
                <div className={`${col.headerBg} px-4 py-3 flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${col.headerText}`} />
                    <span className={`text-sm font-bold ${col.headerText}`}>{col.title}</span>
                  </div>
                  <span className={`${col.badgeBg} text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[24px] text-center`}>
                    {col.items.length}
                  </span>
                </div>
                <div className="p-3 space-y-3 max-h-96 overflow-y-auto bg-white/50 dark:bg-dark-500/30">
                  {col.items.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 dark:text-gray-500 text-sm italic">No items</div>
                  ) : (
                    col.items.map((item: any, idx: number) => (
                      <KanbanCard key={idx} item={item} jobName={job.jobName} columnType={col.title} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render a single job row
  const renderJobRow = (job: SchedulerJob) => {
    const Icon = getJobIcon(job.jobName);
    const colors = getJobColor(job.jobName);
    const isExpanded = expandedJobId === job.jobId;
    const kanban = getKanbanData(job);

    return (
      <div
        key={job.jobId}
        className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden transition-all"
      >
        <button
          onClick={() => toggleJobExpand(job)}
          className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-400/40 transition-colors"
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className={`p-2 ${colors.bg} rounded-lg border ${colors.border} flex-shrink-0`}>
              <Icon className={`h-5 w-5 ${colors.icon}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{job.jobName}</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(job.status)}`}>
                  {job.status}
                </span>
                {job.jobName.includes('match-tick') && job.details?.state && (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                      job.details.isComplete
                        ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                        : job.details.isLive
                          ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/30'
                          : 'bg-gray-50 dark:bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-500/30'
                    }`}
                    title="Match state at the time of this tick"
                  >
                    {job.details.state}
                  </span>
                )}
                {job.jobName.includes('match-tick') && job.details?.waitingForMom && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30"
                    title="Cricbuzz has not yet published the Man-of-the-Match award; final scoring is held back until it lands or the wait limit expires."
                  >
                    <FaInfoCircle className="h-3 w-3" /> Waiting for MoM
                  </span>
                )}
                {job.jobName.includes('match-tick') && job.details?.matchId != null && (
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 font-mono" title="Cricbuzz match id">
                    #{job.details.matchId}
                  </span>
                )}
                {job.duration && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{job.duration}</span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                <span>{formatDate(job.startedAt)}</span>
                {job.message && (
                  <span className="truncate max-w-xs">{job.message}</span>
                )}
              </div>
            </div>
          </div>

          {/* Summary Badges */}
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            {kanban && kanban.succeeded.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                <FaCheckCircle className="h-3 w-3" /> {kanban.succeeded.length}
              </span>
            )}
            {kanban && kanban.failed.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400">
                <FaTimesCircle className="h-3 w-3" /> {kanban.failed.length}
              </span>
            )}
            {kanban && kanban.skipped.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400">
                <FaForward className="h-3 w-3" /> {kanban.skipped.length}
              </span>
            )}
            {isExpanded ? <FaAngleUp className="h-5 w-5 text-gray-400" /> : <FaAngleDown className="h-5 w-5 text-gray-400" />}
          </div>
        </button>

        {isExpanded && (
          <div className="px-6 pb-6 border-t border-gray-100 dark:border-dark-300/30">
            {renderKanbanBoard(job)}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <FaSyncAlt className="h-8 w-8 text-blue-500 animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading scheduler jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-indigo-100 dark:bg-indigo-500/20 rounded-lg border border-indigo-200 dark:border-indigo-500/30">
            <FiClock className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Scheduler Jobs</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {pagination.total} total job(s) &middot; {activeJobs.length} active
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-2">
          {/* View mode toggle */}
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-dark-300 overflow-hidden">
            <button
              onClick={() => { setViewMode('timeline'); setExpandedJobId(null); }}
              className={`px-3 py-2 text-xs font-semibold transition-colors ${
                viewMode === 'timeline'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-dark-400 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-300'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => { setViewMode('list'); setExpandedJobId(null); }}
              className={`px-3 py-2 text-xs font-semibold transition-colors border-l border-gray-300 dark:border-dark-300 ${
                viewMode === 'list'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-dark-400 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-300'
              }`}
            >
              List
            </button>
          </div>
          {/* Job-type filter — applies in both modes */}
          <select
            value={jobFilter}
            onChange={(e) => { setJobFilter(e.target.value); setExpandedJobId(null); }}
            className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            {JOB_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {/* Series filter — derived from fetched jobs' details.seriesId */}
          <select
            value={seriesFilter}
            onChange={(e) => {
              setSeriesFilter(e.target.value);
              setMatchFilter('');
              setExpandedJobId(null);
            }}
            disabled={seriesOptions.length === 0}
            className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
          >
            <option value="">All series</option>
            {seriesOptions.map((s) => (
              <option key={s.seriesId} value={s.seriesId}>{s.name}</option>
            ))}
          </select>
          {/* Match filter — narrows to a single match */}
          <select
            value={matchFilter}
            onChange={(e) => { setMatchFilter(e.target.value); setExpandedJobId(null); }}
            disabled={matchOptions.length === 0}
            className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
          >
            <option value="">All matches</option>
            {matchOptions.map((m) => (
              <option key={m.matchId} value={m.matchId}>{m.label}</option>
            ))}
          </select>
          {/* Page-size picker */}
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            title="Rows per page"
            className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            {[50, 100, 200, 500].map((n) => (
              <option key={n} value={n}>{n}/page</option>
            ))}
          </select>
          {viewMode === 'timeline' && (
            <label className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 hover:bg-gray-50 dark:hover:bg-dark-300">
              <input
                type="checkbox"
                checked={errorsOnly}
                onChange={(e) => setErrorsOnly(e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              Errors only
            </label>
          )}
          <button
            onClick={() => loadJobs(pagination.page)}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50"
          >
            <FaSyncAlt className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-dark-300/40">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setExpandedJobId(null); }}
                className={`flex items-center gap-2 px-6 py-3.5 text-sm font-semibold transition-all relative ${
                  isActive
                    ? 'text-indigo-700 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                <span>{tab.label}</span>
                <span className={`ml-1 px-2 py-0.5 text-xs font-bold rounded-full ${
                  isActive
                    ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400'
                    : 'bg-gray-100 dark:bg-dark-300/50 text-gray-500 dark:text-gray-400'
                }`}>
                  {tab.count}
                </span>
                {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400" />}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {/* All Jobs Tab */}
          {activeTab === 'all' && (
            <div className="space-y-4">
              {jobs.length === 0 && !error ? (
                <div className="text-center py-12">
                  <FiClock className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Jobs Found</h3>
                  <p className="text-gray-600 dark:text-gray-400">No scheduler job runs have been recorded yet.</p>
                </div>
              ) : (
                <>
                  {viewMode === 'timeline' ? renderTimeline() : filteredJobs.map((job) => renderJobRow(job))}

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-dark-300/40">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} jobs)
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400 border border-gray-300 dark:border-dark-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <FaAngleLeft className="h-4 w-4 mr-1" /> Prev
                        </button>
                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= pagination.totalPages}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400 border border-gray-300 dark:border-dark-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          Next <FaAngleRight className="h-4 w-4 ml-1" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Active Jobs Tab */}
          {activeTab === 'active' && (
            <div className="space-y-4">
              {activeJobs.length === 0 ? (
                <div className="text-center py-12">
                  <FaBolt className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Active Jobs</h3>
                  <p className="text-gray-600 dark:text-gray-400">There are no jobs currently running.</p>
                </div>
              ) : (
                activeJobs.map((job) => {
                  const Icon = getJobIcon(job.jobName);
                  return (
                    <div key={job.jobId} className="bg-white dark:bg-dark-400/60 rounded-xl border-2 border-blue-300 dark:border-primary-500/40 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 flex items-center gap-4">
                        <div className="relative flex-shrink-0">
                          <div className="p-2 bg-blue-50 dark:bg-primary-500/10 rounded-lg border border-blue-200 dark:border-primary-500/20">
                            <Icon className="h-5 w-5 text-blue-600 dark:text-primary-400" />
                          </div>
                          <div className="absolute -top-1 -right-1 h-3 w-3">
                            <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-75" />
                            <div className="absolute inset-0 bg-blue-500 rounded-full" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="text-base font-bold text-gray-900 dark:text-white">{job.jobName}</h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border-blue-200 dark:border-primary-500/30">
                              Running
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span>Started: {formatDate(job.startedAt)}</span>
                            {job.message && <span>{job.message}</span>}
                          </div>
                        </div>
                        {job.details?.series && job.details.series.length > 0 && (
                          <div className="flex flex-wrap gap-1 flex-shrink-0">
                            {job.details.series.map((s: any) => (
                              <span key={s.seriesId} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 dark:bg-primary-500/10 text-blue-600 dark:text-primary-400 border border-blue-100 dark:border-primary-500/20">
                                {s.seriesName}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Individual Kanban Card Component
const KanbanCard: React.FC<{ item: any; jobName: string; columnType: string }> = ({ item, jobName, columnType }) => {
  const isMatchTick = jobName.includes('match-tick');
  const isSyncScorecard = jobName.includes('sync-scorecard');
  const isFantasyPoints = jobName.includes('fantasy-points');

  // Match-tick: each item is a contest that was scored/skipped for a live/final match tick
  if (isMatchTick && columnType === 'Succeeded') {
    return (
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-bold text-gray-800 dark:text-gray-200 truncate">
            {item.contestName || item.contestId || 'Contest'}
          </span>
          {item.live && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400">
              <span className="relative flex h-1 w-1">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-1 w-1 bg-rose-500" />
              </span>
              LIVE
            </span>
          )}
        </div>
        {item.matchDesc && (
          <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{item.matchDesc}</div>
        )}
        {item.seriesName && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400">{item.seriesName}</div>
        )}
      </div>
    );
  }

  if (isMatchTick && (columnType === 'Skipped' || columnType === 'Failed')) {
    return (
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm">
        <div className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-1 truncate">
          {item.contestName || item.contestId || 'Contest'}
        </div>
        {item.matchDesc && (
          <div className="text-[11px] text-gray-700 dark:text-gray-300">{item.matchDesc}</div>
        )}
        {item.seriesName && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">{item.seriesName}</div>
        )}
        {item.reason && (
          <div className="text-[10px] text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-500/10 rounded px-2 py-1 mt-1 break-words">
            {item.reason}
          </div>
        )}
      </div>
    );
  }

  // Sync Scorecard succeeded card
  if (isSyncScorecard && columnType === 'Succeeded') {
    return (
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono font-bold text-gray-700 dark:text-gray-300">#{item.matchId}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-semibold">
            {item.matchDesc || `Match ${item.matchOrderId}`}
          </span>
        </div>
        {(item.localTeam || item.visitorTeam) && (
          <div className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
            <span className="bg-gray-100 dark:bg-dark-300/50 px-2 py-0.5 rounded text-xs">{item.localTeam || '?'}</span>
            <span className="text-xs text-gray-400">vs</span>
            <span className="bg-gray-100 dark:bg-dark-300/50 px-2 py-0.5 rounded text-xs">{item.visitorTeam || '?'}</span>
          </div>
        )}
        <div className="text-[10px] text-gray-500 dark:text-gray-400">{item.seriesName}</div>
      </div>
    );
  }

  // Fantasy Points succeeded card
  if (isFantasyPoints && columnType === 'Succeeded') {
    return (
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="mb-1">
          <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{item.contestName}</span>
        </div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">{item.seriesName}</div>

        {item.newMatches && item.newMatches.length > 0 && (
          <div className="mb-2">
            <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              New Matches ({item.newMatchCount})
            </p>
            <div className="space-y-1">
              {item.newMatches.map((m: any) => (
                <div key={m.matchId} className="flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300 bg-emerald-50 dark:bg-emerald-500/10 rounded px-2 py-1">
                  <span className="font-mono text-[10px]">#{m.matchId}</span>
                  <span>{m.matchDesc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {item.result && (
          <div className="grid grid-cols-3 gap-1 mt-2">
            <div className="text-center p-1 bg-gray-50 dark:bg-dark-300/30 rounded">
              <p className="text-xs font-bold text-gray-800 dark:text-gray-200">{item.result.totalMatches}</p>
              <p className="text-[9px] text-gray-500">Total</p>
            </div>
            <div className="text-center p-1 bg-emerald-50 dark:bg-emerald-500/10 rounded">
              <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400">{item.result.processed}</p>
              <p className="text-[9px] text-emerald-600 dark:text-emerald-500">Processed</p>
            </div>
            <div className="text-center p-1 bg-red-50 dark:bg-red-500/10 rounded">
              <p className="text-xs font-bold text-red-700 dark:text-red-400">{item.result.failed}</p>
              <p className="text-[9px] text-red-600 dark:text-red-500">Failed</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fantasy Points / sync-scorecard skipped card
  if (columnType === 'Skipped') {
    return (
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="mb-1">
          <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{item.contestName || item.matchDesc || 'Item'}</span>
        </div>
        {item.seriesName && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">{item.seriesName}</div>
        )}
        {item.alreadyProcessed != null && (
          <div className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 rounded px-2 py-1">
            <FaInfoCircle className="h-3 w-3 flex-shrink-0" />
            <span>Already processed ({item.alreadyProcessed}/{item.completedMatches} matches)</span>
          </div>
        )}
      </div>
    );
  }

  // Generic fallback
  return (
    <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
      <pre className="text-[10px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words overflow-hidden max-h-32">
        {JSON.stringify(item, null, 2)}
      </pre>
    </div>
  );
};

export default SchedulerJobs;
